/**
 * Twilio SendGrid Marketing Campaigns pricing (official public rates).
 * Source: https://www.twilio.com/en-us/products/marketing-campaigns/pricing
 * Verified: June 2026
 */

export const SENDGRID_PRICING_SOURCE_URL =
  "https://www.twilio.com/en-us/products/marketing-campaigns/pricing";

export type SendGridMarketingPlanId = "free" | "basic" | "advanced";

export interface SendGridMarketingPlan {
  id: SendGridMarketingPlanId;
  name: string;
  monthlyUsd: number;
  contactLimit: number;
  emailLimit: number;
  overagePerUnitUsd: number | null;
}

export const SENDGRID_MARKETING_PLANS: SendGridMarketingPlan[] = [
  {
    id: "free",
    name: "Free Marketing",
    monthlyUsd: 0,
    contactLimit: 2000,
    emailLimit: 6000,
    overagePerUnitUsd: null,
  },
  {
    id: "basic",
    name: "Basic",
    monthlyUsd: 15,
    contactLimit: 100_000,
    emailLimit: 300_000,
    overagePerUnitUsd: 0.0023,
  },
  {
    id: "advanced",
    name: "Advanced",
    monthlyUsd: 60,
    contactLimit: 200_000,
    emailLimit: 1_000_000,
    overagePerUnitUsd: 0.005,
  },
];

export interface SendGridPlanCostBreakdown {
  plan: SendGridMarketingPlan;
  eligible: boolean;
  baseMonthlyUsd: number;
  contactOverageUnits: number;
  emailOverageUnits: number;
  overageMonthlyUsd: number;
  totalMonthlyUsd: number;
  fitsWithoutOverage: boolean;
}

export interface SendGridCostEstimate {
  subscribers: number;
  monthlySends: number;
  /** Primary line shown in UI — Basic plan + official overages (Wembii default). */
  primary: SendGridPlanCostBreakdown;
  /** Cheapest Twilio plan for these volumes. */
  recommended: SendGridPlanCostBreakdown;
  free: SendGridPlanCostBreakdown;
  basic: SendGridPlanCostBreakdown;
  advanced: SendGridPlanCostBreakdown;
  savingsWithRecommended: number;
}

export interface SendGridCostScenario {
  id: string;
  labelKey: string;
  subscribers: number;
  monthlySends: number;
}

export const SENDGRID_COST_SCENARIOS: SendGridCostScenario[] = [
  {
    id: "newsletter-small",
    labelKey: "app.sendgridBilling.calculator.scenario.newsletterSmall",
    subscribers: 800,
    monthlySends: 3200,
  },
  {
    id: "smb-basic",
    labelKey: "app.sendgridBilling.calculator.scenario.smbBasic",
    subscribers: 5000,
    monthlySends: 15_000,
  },
  {
    id: "weekly-campaigns",
    labelKey: "app.sendgridBilling.calculator.scenario.weeklyCampaigns",
    subscribers: 25_000,
    monthlySends: 100_000,
  },
  {
    id: "high-volume",
    labelKey: "app.sendgridBilling.calculator.scenario.highVolume",
    subscribers: 100_000,
    monthlySends: 350_000,
  },
];

export function parseCalculatorInput(value: string): number {
  const normalized = String(value || "")
    .trim()
    .replace(/\s/g, "")
    .replace(/,/g, "");
  if (!normalized) {
    return 0;
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return 0;
  }
  return Math.floor(parsed);
}

function computePlanCost(
  plan: SendGridMarketingPlan,
  subscribers: number,
  monthlySends: number
): SendGridPlanCostBreakdown {
  if (plan.id === "free") {
    const eligible =
      subscribers <= plan.contactLimit && monthlySends <= plan.emailLimit;
    return {
      plan,
      eligible,
      baseMonthlyUsd: 0,
      contactOverageUnits: 0,
      emailOverageUnits: 0,
      overageMonthlyUsd: 0,
      totalMonthlyUsd: 0,
      fitsWithoutOverage: eligible,
    };
  }

  const contactOverageUnits = Math.max(0, subscribers - plan.contactLimit);
  const emailOverageUnits = Math.max(0, monthlySends - plan.emailLimit);
  const overageRate = plan.overagePerUnitUsd ?? 0;
  const overageMonthlyUsd =
    (contactOverageUnits + emailOverageUnits) * overageRate;

  return {
    plan,
    eligible: true,
    baseMonthlyUsd: plan.monthlyUsd,
    contactOverageUnits,
    emailOverageUnits,
    overageMonthlyUsd,
    totalMonthlyUsd: plan.monthlyUsd + overageMonthlyUsd,
    fitsWithoutOverage: contactOverageUnits === 0 && emailOverageUnits === 0,
  };
}

export function estimateSendGridMarketingCost(
  subscribersInput: number,
  monthlySendsInput: number
): SendGridCostEstimate {
  const subscribers = parseCalculatorInput(String(subscribersInput));
  const monthlySends = parseCalculatorInput(String(monthlySendsInput));

  const free = computePlanCost(
    SENDGRID_MARKETING_PLANS[0],
    subscribers,
    monthlySends
  );
  const basic = computePlanCost(
    SENDGRID_MARKETING_PLANS[1],
    subscribers,
    monthlySends
  );
  const advanced = computePlanCost(
    SENDGRID_MARKETING_PLANS[2],
    subscribers,
    monthlySends
  );

  const paidOptions = [basic, advanced];
  const recommended = paidOptions.reduce((best, current) =>
    current.totalMonthlyUsd < best.totalMonthlyUsd ? current : best
  );

  const primary =
    free.eligible && free.totalMonthlyUsd < basic.totalMonthlyUsd
      ? free
      : basic;

  return {
    subscribers,
    monthlySends,
    primary,
    recommended:
      free.eligible && free.totalMonthlyUsd <= recommended.totalMonthlyUsd
        ? free
        : recommended,
    free,
    basic,
    advanced,
    savingsWithRecommended: Math.max(
      0,
      primary.totalMonthlyUsd - recommended.totalMonthlyUsd
    ),
  };
}

export function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}
